import { defineStore } from 'pinia';
import axios from '@/axios';
import router from '@/router';
import { usePermissionStore } from '@/stores/permission';

// Storage keys used by allauth headless (app mode).
const SESSION_TOKEN_KEY = 'pfSessionToken';
const USER_PROFILE_KEY = 'userProfile';
// Separate key for pre-auth flow tokens (password reset, etc.) so they
// never get confused with the main authenticated session token.
const PREAUTH_TOKEN_KEY = 'pfPreAuthToken';

// Legacy keys from the previous simplejwt implementation.
// Cleared on app init so stale tokens don't leak into request headers.
const LEGACY_KEYS = ['accessToken', 'refreshToken', 'rememberMe'];

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    sessionToken: null,
    authModalVisible: false,
    authModalMode: 'login', // 'login' | 'signup' | 'verify'
    authModalMessage: '',
    // Pre-auth session while the user is in the verify_email flow.
    pendingVerification: false,
    pendingEmail: '',
  }),

  getters: {
    isAuthenticated: (state) => !!state.sessionToken && !!state.user,
  },

  actions: {
    // ---------------------------------------------------------------
    // Session token helpers
    // ---------------------------------------------------------------
    setSession(token, user) {
      this.sessionToken = token || null;
      this.user = user || null;
      if (token) {
        localStorage.setItem(SESSION_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
      if (user) {
        localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(user));
        this._syncCurrencyOnLogin(user);
      } else {
        localStorage.removeItem(USER_PROFILE_KEY);
      }
    },

    _syncCurrencyOnLogin(user) {
      const accountCurrency = user?.preferred_currency;
      const localCurrency = localStorage.getItem('preferredCurrency');
      const isManual = localStorage.getItem('currencySource') === 'manual';

      if (accountCurrency) {
        // Account wins — apply to browser
        localStorage.setItem('currency', accountCurrency);
        localStorage.setItem('preferredCurrency', accountCurrency);
        localStorage.setItem('currencySource', 'manual');
        window.dispatchEvent(new CustomEvent('currency-updated', {
          detail: { currency: accountCurrency },
        }));
      } else if (isManual && localCurrency) {
        // Browser has a manual choice but account is empty — push to account
        this.savePreferredCurrency(localCurrency);
      }
    },

    clearSession() {
      this.sessionToken = null;
      this.user = null;
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(USER_PROFILE_KEY);
    },

    // ---------------------------------------------------------------
    // Allauth endpoints
    // ---------------------------------------------------------------
    /**
     * Log in via POST /_allauth/app/v1/auth/login.
     * Accepts {email|username, password}. The caller can pass either one;
     * we detect email by '@' and send the matching field key.
     */
    async login({ identifier, email, username, password }) {
      const payload = { password };
      const ident = identifier || email || username || '';
      if (ident.includes('@')) {
        payload.email = ident;
      } else {
        payload.username = ident;
      }

      try {
        const { data } = await axios.post('/_allauth/app/v1/auth/login', payload);
        const token = data?.meta?.session_token;
        const user = data?.data?.user;
        if (!token || !user) {
          console.error('Login response missing session_token or user', data);
          return { success: false, errors: [{ message: 'Login response was incomplete. Please try again.' }] };
        }
        this.setSession(token, user);
        try {
          const permissionStore = usePermissionStore();
          await permissionStore.checkUserPermissions();
        } catch (err) {
          console.warn('Permission store sync failed after login:', err);
        }
        return { success: true };
      } catch (err) {
        // allauth returns 401 + { flows: [{id:"verify_email", is_pending:true}] }
        // when the account exists but isn't verified yet. Treat it as a pending flow.
        const pending = this._detectPendingVerification(err.response?.data);
        if (pending) {
          this._enterPendingVerification(err.response.data, ident.includes('@') ? ident : '');
          return { success: true, pendingVerification: true, email: this.pendingEmail };
        }
        console.error('Login failed:', err.response?.data || err);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'The email/username and password you entered do not match our records.' }],
        };
      }
    },

    /**
     * Register via POST /_allauth/app/v1/auth/signup.
     * Payload keys match the CustomSignupForm: username, email, password,
     * full_name, agree_to_terms.
     */
    async register(signupData) {
      const payload = {
        username: signupData.username,
        email: signupData.email,
        password: signupData.password,
        full_name: signupData.full_name,
        agree_to_terms: !!signupData.agree_to_terms,
      };
      try {
        const { data } = await axios.post('/_allauth/app/v1/auth/signup', payload);
        // No verification path (e.g. ACCOUNT_EMAIL_VERIFICATION=none|optional):
        // we get 200 with a session_token and user — log in immediately.
        const token = data?.meta?.session_token;
        const user = data?.data?.user;
        if (token && user) {
          this.setSession(token, user);
          return { success: true };
        }
        console.error('Signup response missing session_token or user', data);
        return { success: false, errors: data?.errors || [] };
      } catch (err) {
        // Mandatory verification path: allauth returns 401 with a pre-auth
        // session_token + { flows: [{id:"verify_email", is_pending:true}] }.
        const pending = this._detectPendingVerification(err.response?.data);
        if (pending) {
          this._enterPendingVerification(err.response.data, signupData.email);
          return { success: true, pendingVerification: true, email: this.pendingEmail };
        }
        console.error('Registration failed:', err.response?.data || err);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'Registration failed' }],
        };
      }
    },

    /**
     * Internal: detect `verify_email` pending flow in an allauth response body.
     */
    _detectPendingVerification(data) {
      const flows = data?.data?.flows;
      if (!Array.isArray(flows)) return false;
      return flows.some((f) => f?.id === 'verify_email' && f?.is_pending);
    },

    /**
     * Internal: persist the pre-auth session token so we can call
     * /auth/email/verify with `X-Session-Token`, and switch the modal.
     */
    _enterPendingVerification(data, email) {
      const token = data?.meta?.session_token;
      if (token) {
        this.sessionToken = token;
        localStorage.setItem(SESSION_TOKEN_KEY, token);
      }
      this.pendingVerification = true;
      this.pendingEmail = email || '';
      this.authModalMode = 'verify';
      this.authModalVisible = true;
    },

    /**
     * Exchange a 6-digit code for a full authenticated session.
     * POST /_allauth/app/v1/auth/email/verify  { key: code }
     */
    async verifyEmailCode(code) {
      const key = (code || '').toString().replace(/\s+/g, '');
      try {
        const { data } = await axios.post('/_allauth/app/v1/auth/email/verify', { key });
        const token = data?.meta?.session_token;
        const user = data?.data?.user;
        if (token && user) {
          this.setSession(token, user);
          this.pendingVerification = false;
          this.pendingEmail = '';
          try {
            const permissionStore = usePermissionStore();
            await permissionStore.checkUserPermissions();
          } catch (err) {
            console.warn('Permission store sync failed after verify:', err);
          }
          return { success: true };
        }
        return { success: false, errors: data?.errors || [{ message: 'Verification did not return a session.' }] };
      } catch (err) {
        console.error('Email verification failed:', err.response?.data || err);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'Invalid or expired code.' }],
        };
      }
    },

    /**
     * Ask allauth to email a new verification code.
     * POST /_allauth/app/v1/auth/email/verify/resend
     */
    async resendVerificationCode() {
      try {
        await axios.post('/_allauth/app/v1/auth/email/verify/resend', {});
        return { success: true };
      } catch (err) {
        console.error('Resend verification failed:', err.response?.data || err);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'Could not resend the code.' }],
        };
      }
    },

    /**
     * Verify or hydrate the current session via GET /_allauth/app/v1/auth/session.
     * Called on app boot when a session token is present in localStorage.
     */
    async fetchSession() {
      if (!this.sessionToken) return false;
      try {
        const { data } = await axios.get('/_allauth/app/v1/auth/session');
        const user = data?.data?.user;
        if (user) {
          this.user = user;
          localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(user));
          return true;
        }
        this.clearSession();
        return false;
      } catch (err) {
        // 401 + pending verify_email means the user refreshed during the
        // code step — resume the verify modal instead of wiping state.
        if (err.response?.status === 401 && this._detectPendingVerification(err.response?.data)) {
          this._enterPendingVerification(err.response.data, this.pendingEmail);
          return false;
        }
        if (err.response?.status === 401 || err.response?.status === 410) {
          this.clearSession();
        } else {
          console.warn('Session hydration failed (non-fatal):', err.message);
        }
        return false;
      }
    },

    /**
     * Log out via DELETE /_allauth/app/v1/auth/session. Always clears local
     * state, even if the backend call fails.
     */
    async logout() {
      const hadSession = !!this.sessionToken;
      if (hadSession) {
        try {
          await axios.delete('/_allauth/app/v1/auth/session');
        } catch (err) {
          // 401 is expected if the token has already expired server-side.
          if (err.response?.status !== 401) {
            console.warn('Logout request failed (clearing locally anyway):', err.message);
          }
        }
      }
      this.clearSession();
      try {
        const permissionStore = usePermissionStore();
        if (typeof permissionStore.reset === 'function') permissionStore.reset();
      } catch (_) { /* ignore */ }
      router.push('/');
    },

    /**
     * Request a password reset email. Allauth returns a session_token
     * that ties the reset flow to this browser; stash it so the
     * follow-up confirm can carry it.
     */
    async requestPasswordReset(email) {
      try {
        const { data } = await axios.post('/_allauth/app/v1/auth/password/request', { email });
        const preAuthToken = data?.meta?.session_token;
        if (preAuthToken) {
          localStorage.setItem(PREAUTH_TOKEN_KEY, preAuthToken);
        }
        return { success: true };
      } catch (err) {
        console.error('Password reset request failed:', err.response?.data || err);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'Request failed' }],
        };
      }
    },

    /**
     * Consume a password reset key with a new password. Reuses the
     * pre-auth token from the earlier ``password/request`` call so
     * allauth recognises this as a continuation of the same flow.
     */
    async confirmPasswordReset(key, password) {
      try {
        const headers = {};
        const preAuthToken = localStorage.getItem(PREAUTH_TOKEN_KEY);
        if (preAuthToken) headers['X-Session-Token'] = preAuthToken;
        const { data } = await axios.post(
          '/_allauth/app/v1/auth/password/reset',
          { key, password },
          { headers },
        );
        // Pre-auth token has served its purpose; drop it either way.
        localStorage.removeItem(PREAUTH_TOKEN_KEY);
        // On success allauth logs the user in and returns a session_token.
        const token = data?.meta?.session_token;
        const user = data?.data?.user;
        if (token && user) {
          this.setSession(token, user);
        }
        return { success: true };
      } catch (err) {
        console.error('Password reset confirm failed:', err.response?.data || err);
        // Token may have been rotated / burned; clear so we don't reuse it.
        localStorage.removeItem(PREAUTH_TOKEN_KEY);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'Reset failed' }],
        };
      }
    },

    /**
     * Confirm an email verification key.
     */
    async verifyEmail(key) {
      try {
        await axios.post('/_allauth/app/v1/auth/email/verify', { key });
        // Re-fetch session so email_verified reflects on the user object.
        await this.fetchSession();
        return { success: true };
      } catch (err) {
        console.error('Email verification failed:', err.response?.data || err);
        return {
          success: false,
          errors: err.response?.data?.errors || [{ message: 'Verification failed' }],
        };
      }
    },

    // ---------------------------------------------------------------
    // Preferences
    // ---------------------------------------------------------------
    async savePreferredCurrency(currencyCode) {
      if (!this.isAuthenticated) return;
      try {
        await axios.patch('/auth/profile/currency/', {
          preferred_currency: currencyCode,
        });
        if (this.user) {
          this.user.preferred_currency = currencyCode;
          localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(this.user));
        }
      } catch (err) {
        console.warn('Failed to sync preferred currency to account:', err);
      }
    },

    // ---------------------------------------------------------------
    // Modal helpers (unchanged API for existing components)
    // ---------------------------------------------------------------
    openAuthModal(mode = 'login', message = '') {
      this.authModalMode = mode;
      this.authModalMessage = message || '';
      this.authModalVisible = true;
    },

    closeAuthModal() {
      this.authModalVisible = false;
      this.authModalMessage = '';
    },

    // ---------------------------------------------------------------
    // App boot
    // ---------------------------------------------------------------
    /**
     * Called once from main.js on app startup.
     * - Wipes any legacy simplejwt tokens from localStorage.
     * - Rehydrates user + session from localStorage.
     * - Fires a session probe to confirm the token is still valid.
     */
    async initializeAuth() {
      // Clean up legacy JWT pair left over from the pre-allauth era.
      LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));

      const token = localStorage.getItem(SESSION_TOKEN_KEY);
      const cachedUser = localStorage.getItem(USER_PROFILE_KEY);
      if (!token) return;

      this.sessionToken = token;
      if (cachedUser) {
        try { this.user = JSON.parse(cachedUser); } catch (_) { this.user = null; }
      }
      await this.fetchSession();
    },
  },
});
